/* eslint-disable jsx-a11y/interactive-supports-focus */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useState, useEffect, useCallback } from 'react';
import debounce from 'lodash/debounce';
import {
  Card,
  Container,
  Tabs,
  Tab,
  TableContainer,
  Table,
  TableBody,
  Box,
  Slider,
  Typography,
  Popover,
  TextField,
  Button,
  IconButton,
  Paper,
  Icon,
  InputAdornment,
  Input,
  CircularProgress,
} from '@mui/material';
import { toast } from 'react-toastify';
import { useNavigate, useLocation } from 'react-router';
import ClearIcon from '@mui/icons-material/Clear';
import { Header, ProjectFormModal, ProjectListToolbar } from '.';
import {
  getProjects,
  createProject,
  updateProject,
  getProjectsNames,
  getFavoriteProject,
  getProjectCount,
} from '../../../../services/projectService';
import { getAllClients } from '../../../../services/clientService';
import { DEFAULT_PROJECT_STATUS, PROJECT_EDITOR_ROLE } from '../../../../Constants/ProjectConstants';
import { DEFAULT_TABLE_PAGE_LIMIT } from '../../../../Constants/table.constants';
import { isAdmin } from '../../../../utils/share';
import ConfirmModal from './ConfirmModal';
import BreadCrumb from '../../../../components/breadcrumb/BreadCrumbNavigation';
import {
  useTable,
  emptyRows,
  TableNoData,
  TableEmptyRows,
  TableHeadCustom,
  TablePaginationCustom,
  TableSkeleton,
} from '../../../../components/table';
import Label from '../../../../components/label';
import ProjectTableRow from './ProjectTableRow';
import ProjectTableFiltersResult from './ProjectTableFiltersResult';
import { useStyles } from '../styles/projectTableStyles';
import { ProgressFilterTab } from './ProgressFilterTab';
import Loading from '../../../../layouts/loading';

function ProjectTable() {
  const navigate = useNavigate();
  const classes = useStyles();
  const [openFormModal, setOpenFormModal] = useState(false);
  const [clientList, setClientList] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: DEFAULT_TABLE_PAGE_LIMIT,
  });
  const [openPopup, setOpenPopup] = useState(false);
  const [action, setAction] = useState('');
  const [status, setStatus] = useState('active');
  const [currentTab, setCurrentTab] = useState('active');
  const [tableData, setTableData] = useState({ rows: [], count: 0 });
  const [projectData, setProjectData] = useState([]);
  const [projectName, setProjectName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('projectName');
  const [projectCounts, setProjectCounts] = useState({
    favoriteCount: 0,
    activeCount: 0,
    archiveCount: 0,
    completeCount: 0,
    holdCount: 0,
    allCount: 0,
  });
  const [favoriteProjectIds, setFavoriteProjectIds] = useState([]);
  const [progressFilter, setProgressFilter] = useState(0);
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const healthFilterFromCard = queryParams.get('health');

  const handleProgressChange = (event, newValue) => {
    setProgressFilter(newValue);
  };

  const handleFetchApiCall = (event, newValue) => {
    setColumnFilters((prevState) => {
      const updatedFilters = prevState.filter((filter) => filter.id !== 'progress');

      return [...updatedFilters, { id: 'progress', value: newValue }];
    });
  };
  const TABLE_HEAD = [
    isAdmin() && { id: '', label: '' },
    { id: 'projectName', label: 'Project Name', Sorting: true },
    { id: '', label: 'Client' },
    { id: 'totalHrs', label: 'Budgeted Hours' },
    { id: '', label: 'Consumed Hours' },
    {
      id: 'progress',
      label: 'Progress',
      render: () => (
        <>
          <Typography sx={{ fontSize: '14px' }}>
            Progress
            <Typography sx={{ fontSize: '10px' }}>More than ({progressFilter ?? 0}%)</Typography>
          </Typography>
        </>
      ),
    },
    { id: '', label: 'Status' },
    { id: '', label: 'Action', align: 'center' },
  ].filter(Boolean);

  const defaultFilters = {
    progress: 0,
    projectName: null,
    totalHrs: '',
    total_time: '',
    globalFilter: '',
    startDate: null,
    endDate: null,
    status: '',
    health: null,
  };

  const table = useTable({ defaultOrderBy: 'createDate' });

  const [filters, setFilters] = useState(defaultFilters);

  const dateError =
    filters.startDate && filters.endDate ? filters.startDate.getTime() > filters.endDate.getTime() : false;

  const handleSorting = useCallback(
    (id) => {
      if (id) {
        const isAsc = orderBy === id && order === 'asc';
        const newOrder = isAsc ? 'desc' : 'asc';

        setOrder(newOrder);
        setOrderBy(id);

        setSorting([
          {
            id,
            desc: newOrder === 'desc',
          },
        ]);
      }
    },

    [order, orderBy]
  );

  // eslint-disable-next-line consistent-return
  const fetchTableData = async () => {
    try {
      setIsLoading(true);

      const response = await getProjects({
        start: pagination.pageIndex * pagination.pageSize,
        pageSize: pagination.pageSize,
        filters: JSON.stringify(columnFilters ?? []),
        sorting: JSON.stringify(sorting ?? []),
        globalFilter: filters.globalFilter,
        status,
      });

      setTableData({ rows: response?.data?.data?.rows, count: response?.data?.data?.count });
      setProjectCounts({
        favoriteCount: response?.data?.data?.favoriteCount,
        activeCount: response?.data?.data?.activeCount,
        archiveCount: response?.data?.data?.archiveCount,
        completeCount: response?.data?.data?.completeCount,
        holdCount: response?.data?.data?.holdCount,
        allCount: response?.data?.data?.allCount,
      });
    } catch (error) {
      toast.error('An error occurred while fetching the project list. Please try again later.');
      setTableData({ count: 0, rows: [] });
    } finally {
      setIsLoading(false);
    }
  };

  // const fetchAutocompleteData = useCallback(async (searchTerm) => {
  //   try {
  //     const response = await getProjectsNames({
  //       projectName: searchTerm,
  //       page: 1,
  //       pageSize: 5,
  //       status: currentTab,
  //     });
  //     setProjectData(response?.data?.data);
  //   } catch (error) {
  //     toast.error('An error occurred while fetching the project list. Please try again later.');
  //     setProjectData([]);
  //   }
  // }, []);

  // const debouncedFetchAutocomplete = useCallback(
  //   debounce((searchTerm) => {
  //     fetchAutocompleteData(searchTerm);
  //   }, 300),
  //   [fetchAutocompleteData]
  // );

  useEffect(() => {
    const timeout = setTimeout(
      () => {
        fetchTableData();
      },
      filters.globalFilter ? 1000 : 0
    );

    return () => clearTimeout(timeout); // Cleanup to avoid multiple timeouts
  }, [pagination, filters, status, sorting, columnFilters]);

  // useEffect(() => {
  //   debouncedFetchAutocomplete(projectName);

  //   return () => debouncedFetchAutocomplete.cancel();
  // }, [projectName, debouncedFetchAutocomplete]);

  const getFavoriteProjects = async () => {
    try {
      const response = await getFavoriteProject();
      const projectIds = response.data?.data?.map((item) => item.project.id);
      setFavoriteProjectIds(projectIds);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getFavoriteProjects();
  }, []);

  const denseHeight = table.dense ? 56 : 76;

  const canReset =
    !!filters.projectName ||
    filters.globalFilter ||
    !!filters.totalHrs ||
    filters.total_time ||
    (!!filters.startDate && !!filters.endDate) ||
    !!filters.health;

  const notFound = (!tableData.rows.length && canReset) || !tableData.rows.length;

  const TABS = [
    { value: 'active', label: 'Active', color: 'primary', count: projectCounts.activeCount },
    isAdmin() && {
      value: 'Favorite',
      label: 'Favorite',
      color: 'warning',
      count: projectCounts.favoriteCount,
    },
    {
      value: 'archive',
      label: 'Archived',
      color: 'info',
      count: projectCounts.archiveCount,
    },
    {
      value: 'complete',
      label: 'Completed',
      color: 'success',
      count: projectCounts.completeCount,
    },
    {
      value: 'hold',
      label: 'On Hold',
      color: 'default',
      count: projectCounts.holdCount,
    },
    { value: '', label: 'All', color: 'dark', count: projectCounts.allCount },
  ].filter(Boolean);

  const handleFilters = useCallback(
    (name, value) => {
      setColumnFilters((prevState) => {
        const updatedFilters = prevState.filter((filter) => filter.id !== name);
        if (value === '' || value === null) {
          return updatedFilters;
        }

        return [...updatedFilters, { id: name, value }];
      });
      table.onResetPage();
      setFilters((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    },
    [table]
  );

  const handleResetFilters = useCallback(() => {
    setFilters(defaultFilters);
    setColumnFilters([]);
  }, []);

  const handleFormModalClose = useCallback(() => {
    setOpenFormModal(false);
  }, []);

  const handleCreateClick = () => setOpenFormModal(true);

  const handleFormModalSubmit = useCallback(async (values) => {
    try {
      values.allowOverSpent = !!values.allowOverSpent;
      values.allowManualTask = !!values.allowManualTask;
      values.status = DEFAULT_PROJECT_STATUS;
      delete values?.description;
      const response = await createProject(values);
      toast.success(response?.data?.message);
      navigate(`/project/details/${response?.data?.data?.id}`);
      setOpenFormModal(false);
    } catch (error) {
      toast.error(error?.response?.data?.message ?? error.message);
    }
  }, []);

  useEffect(() => {
    const fetchClientsData = async () => {
      const response = await getAllClients();
      setClientList(response?.data?.data?.rows);
    };

    fetchClientsData();
  }, []);

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedProject(null);
  };

  const isEditPermission =
    isAdmin() || PROJECT_EDITOR_ROLE.includes(selectedProject?.projectmember?.projectRoles?.roleName);

  const handleProjectStatusChange = async (project, status, successMessage, dates = {}) => {
    if (!isEditPermission) {
      toast.error('You do not have permission to perform this action');
      handleCloseMenu();
      return;
    }
    if (selectedProject?.status === status) {
      toast.success(`Project is already ${status}`);
      handleCloseMenu();
      return;
    }
    try {
      await updateProject(project.id, { status, ...dates });
      // refetch();
      toast.success(successMessage);
      handleCloseMenu();
    } catch (error) {
      const errMsg =
        error?.response?.data?.message || 'An error occurred while updating the project. Please try again later.';
      toast.error(errMsg);
    }
  };

  const handleUpdateModalClose = () => setOpenPopup(false);

  const handleUpdate = (values) => {
    if (action === 'complete') {
      handleProjectStatusChange(selectedProject, 'complete', 'Project marked as completed');
      setOpenPopup(false);
    }
    if (action === 'archive') {
      handleProjectStatusChange(selectedProject, 'archive', 'Project marked as archived');
      setOpenPopup(false);
    }
    if (action === 'active') {
      handleProjectStatusChange(selectedProject, 'active', 'Project marked as active');
      setOpenPopup(false);
    }
    if (action === 'hold') {
      const { holdStartDate, holdEndDate } = values;
      handleProjectStatusChange(selectedProject, 'hold', 'Project marked as on hold', { holdStartDate, holdEndDate });
      setOpenPopup(false);
    }
  };

  const handleChangeTab = (event, newValue) => {
    setCurrentTab(newValue);
    setStatus(newValue);
  };

  const onChangeRowsPerPage = (event) => {
    setPagination({ pageIndex: 0, pageSize: parseInt(event.target.value, 10) });
  };

  const onChangePage = (event, newPage) => {
    setPagination({ ...pagination, pageIndex: newPage });
  };
  useEffect(() => {
    if (healthFilterFromCard) {
      setColumnFilters((prev) => [
        ...prev.filter((f) => f.id !== 'health'),
        { id: 'health', value: healthFilterFromCard },
      ]);

      setFilters((prev) => ({
        ...prev,
        health: healthFilterFromCard,
      }));
    }
  }, [healthFilterFromCard]);

  const breadcrumbs = [
    {
      label: 'Home',
      path: '/',
    },
    {
      label: 'Projects',
      path: '',
    },
  ];

  return (
    <>
      <BreadCrumb breadcrumbs={breadcrumbs} />
      <Container maxWidth="4xl">
        <Loading open={isLoading} />
        <Header onCreateClick={handleCreateClick} />
        <Box className={classes.tabsBox}>
          <Tabs
            value={currentTab}
            onChange={handleChangeTab}
            sx={{
              '& .MuiTabs-indicator': {
                backgroundColor: 'black',
              },
              outline: 'none !important',
            }}
          >
            {TABS.map((tab) => (
              <Tab
                key={tab.value}
                value={tab.value}
                label={tab.label}
                iconPosition="end"
                // sx={{ outline: 'none !important' }}
                sx={{
                  outline: 'none !important',
                  '&.Mui-selected': {
                    color: 'black',
                    fontWeight: 'bold',
                  },
                }}
                icon={
                  <Label
                    variant={((tab.value === '' || tab.value === filters.status) && 'filled') || 'soft'}
                    color={tab.color}
                  >
                    {tab.count}
                  </Label>
                }
              />
            ))}
          </Tabs>

          <ProgressFilterTab
            classes={classes}
            onChange={(e) => handleProgressChange(e, e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleFetchApiCall(e, e.target.value);
              }
            }}
            onClear={(e) => {
              handleProgressChange(e, 0);
              handleFetchApiCall(e, 0);
            }}
            progressFilter={progressFilter}
          />
        </Box>
        <Card>
          <ProjectListToolbar
            filters={filters}
            onFilters={handleFilters}
            projectName={projectName}
            setProjectName={setProjectName}
            //
            dateError={dateError}
            projectOption={projectData.map((option) => option.projectName)}
          />

          {isLoading ? (
            <TableSkeleton />
          ) : (
            <>
              {canReset && (
                <ProjectTableFiltersResult
                  filters={filters}
                  onFilters={handleFilters}
                  //
                  onResetFilters={handleResetFilters}
                  //
                  results={tableData.count}
                  sx={{ p: 2.5, pt: 0 }}
                />
              )}
              <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
                <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 800 }}>
                  <TableHeadCustom
                    order={order}
                    orderBy={orderBy}
                    headLabel={TABLE_HEAD}
                    rowCount={tableData.rows.length}
                    numSelected={table.selected.length}
                    onSort={handleSorting}
                  />

                  <TableBody>
                    {tableData.rows.map((row) => (
                      <ProjectTableRow
                        key={row.id}
                        favoriteProjectIds={favoriteProjectIds}
                        row={row}
                        fetchTableData={fetchTableData}
                        refetchHeaderCount={getFavoriteProjects}
                        setProjectCounts={setProjectCounts}
                      />
                    ))}

                    <TableEmptyRows
                      height={denseHeight}
                      emptyRows={emptyRows(table.page, table.rowsPerPage, tableData.rows.length)}
                    />

                    <TableNoData notFound={notFound} />
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePaginationCustom
                count={tableData.count}
                page={pagination.pageIndex}
                rowsPerPage={pagination.pageSize}
                onPageChange={onChangePage}
                onRowsPerPageChange={onChangeRowsPerPage}
                dense={table.dense}
                onChangeDense={table.onChangeDense}
              />
            </>
          )}
        </Card>
        {openFormModal && (
          <ProjectFormModal
            open
            action="create"
            onSubmit={handleFormModalSubmit}
            onClose={handleFormModalClose}
            clientList={clientList}
          />
        )}
      </Container>
      {openPopup && (
        <ConfirmModal
          updateSelected={selectedProject}
          onClose={handleUpdateModalClose}
          onUpdate={handleUpdate}
          action={action}
        />
      )}
    </>
  );
}

export default ProjectTable;
